/**
 * Entrees de la navigation principale et sections des parametres, avec la
 * permission que chacune exige. La coquille et la page Parametres n'affichent
 * que les entrees permises ; les routes appliquent les memes permissions
 * (RequiertPermission) : une adresse saisie a la main aboutit a « Acces
 * reserve », jamais a un ecran vide ou a une erreur de l'API.
 */
import { NavLink } from 'react-router-dom';
import {
  BellRing, Building2, ChartColumn, Headset, Plug, Radar, Settings, Sparkles, SquareKanban, Sun, UsersRound, Workflow,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Permission, Role } from '@leads/shared';
import { satisfait } from '../lib/permissions';
import type { Exigence } from '../lib/permissions';

export interface EntreeNavigation {
  vers: string;
  libelle: string;
  icone: LucideIcon;
  /** L'accueil ne doit etre actif que sur « / », pas sur toutes les pages. */
  exact?: boolean;
  /** Compteur affiche apres le libelle (relances du jour et en retard) ; a zero, rien ne s'affiche. */
  badge?: number;
  /** Permission exigee (une liste : l'une suffit). Absente : tout compte connecte. */
  permission?: Exigence;
}

export interface SectionParametres {
  vers: string;
  libelle: string;
  icone: LucideIcon;
  permission: Permission;
}

/**
 * Les routes des sections sont generees depuis cette liste (`App.tsx`) : la
 * permission qui affiche une section dans la navigation est celle qui garde
 * son adresse.
 */
export const SECTIONS_PARAMETRES = [
  { vers: '/parametres/equipe', libelle: 'Équipe', icone: UsersRound, permission: 'equipe.gerer' },
  { vers: '/parametres/offre', libelle: 'Offre et IA', icone: Sparkles, permission: 'ia.configurer' },
  { vers: '/parametres/pipeline', libelle: 'Pipeline', icone: Workflow, permission: 'pipeline.configurer' },
  { vers: '/parametres/integrations', libelle: 'Intégrations', icone: Plug, permission: 'jetons.gerer' },
] as const satisfies readonly SectionParametres[];

/** Adresse d'une section : chacune doit avoir son ecran, ce que verifie la compilation. */
export type AdresseSection = (typeof SECTIONS_PARAMETRES)[number]['vers'];

/**
 * Parametres s'ouvre a qui peut voir au moins une section : l'entree ne mene
 * jamais a une page vide.
 */
export const PERMISSIONS_PARAMETRES: readonly Permission[] = SECTIONS_PARAMETRES.map(
  (section) => section.permission,
);

/** Entree dont le compteur est le badge des relances (conception 7.5). */
export const CHEMIN_RELANCES = '/relances';

export const NAVIGATION: readonly EntreeNavigation[] = [
  { vers: '/', libelle: "Aujourd'hui", icone: Sun, exact: true },
  { vers: '/leads', libelle: 'Leads', icone: Building2 },
  { vers: '/pipeline', libelle: 'Pipeline', icone: SquareKanban },
  { vers: CHEMIN_RELANCES, libelle: 'Relances', icone: BellRing },
  { vers: '/session', libelle: "Session d'appels", icone: Headset, permission: 'leads.ecrire' },
  { vers: '/statistiques', libelle: 'Statistiques', icone: ChartColumn },
  { vers: '/trouver', libelle: 'Trouver des leads', icone: Radar, permission: 'leads.importer' },
  { vers: '/parametres', libelle: 'Paramètres', icone: Settings, permission: PERMISSIONS_PARAMETRES },
];

/** Pose le nombre de relances du jour et en retard sur l'entree Relances ; 0 n'affiche rien. */
export function avecCompteurRelances<T extends EntreeNavigation>(entrees: readonly T[], nombre: number): T[] {
  return entrees.map((entree) => (entree.vers === CHEMIN_RELANCES ? { ...entree, badge: nombre } : entree));
}

/** Entrees accessibles a un role, dans leur ordre. */
export function entreesAutorisees<T extends { permission?: Exigence }>(
  entrees: readonly T[],
  role: Role | null,
): T[] {
  return entrees.filter((entree) => satisfait(role, entree.permission));
}

export function LienNavigation({ entree }: { entree: EntreeNavigation }) {
  const { vers, libelle, icone: Icone, exact, badge } = entree;
  // Fond et texte actifs ne different des autres entrees que par la teinte : la
  // graisse signale aussi la page courante a qui ne distingue pas les couleurs.
  return (
    <NavLink
      to={vers}
      end={exact}
      className={({ isActive }) =>
        `group flex h-9 items-center gap-2.5 rounded-md px-2.5 text-base transition-colors ${
          isActive
            ? 'bg-nav-actif font-semibold text-nav-texte-actif'
            : 'font-medium text-nav-texte hover:bg-nav-survol hover:text-encre'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icone
            aria-hidden="true"
            className={isActive ? 'text-accent-600' : 'text-encre-tenue group-hover:text-encre-douce'}
          />
          <span className="flex-1 truncate">{libelle}</span>
          {badge !== undefined && badge > 0 && (
            <>
              {/*
               * Le nombre seul ne dit rien a un lecteur d'ecran : le lien se lit « libelle nombre a traiter ».
               * Les espaces sont des noeuds texte entre les elements (jamais rendus dans un conteneur flex ni en
               * fin de ligne) : le nom accessible rogne le texte de chaque element, il ne garde que celles-la.
               */}
              {' '}
              <span className="rounded-full bg-accent-600 px-1.5 text-2xs leading-5 font-semibold text-encre-inverse">
                {badge}
                {' '}
                <span className="sr-only">à traiter</span>
              </span>
            </>
          )}
        </>
      )}
    </NavLink>
  );
}

export function ListeNavigation({ entrees }: { entrees: readonly EntreeNavigation[] }) {
  return (
    <ul className="space-y-0.5">
      {entrees.map((entree) => (
        <li key={entree.vers}>
          <LienNavigation entree={entree} />
        </li>
      ))}
    </ul>
  );
}
